/**
 * FlowContext — estado compartilhado entre as telas do fluxo principal
 * (Login → Câmera → Animais → Identificando → Resultado).
 *
 * Guarda: foto capturada, animais selecionados, planta identificada,
 * avaliação de risco calculada.
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import type {
  PlantFull,
  RiskAssessment,
  SpeciesEnum,
} from '../lib/database.types'

// ============================================================================
// TIPOS
// ============================================================================

export interface CapturedPhoto {
  /** Blob da foto (JPEG) */
  blob: Blob
  /** Data URL para preview imediato */
  dataUrl: string
  /** Source: câmera real ou arquivo uploadado */
  source: 'camera' | 'upload'
}

interface FlowState {
  photo: CapturedPhoto | null
  selectedAnimals: SpeciesEnum[]
  detectedPlant: PlantFull | null
  detectedConfidence: number | null
  risk: RiskAssessment | null
}

interface FlowContextValue extends FlowState {
  setPhoto: (photo: CapturedPhoto | null) => void
  setSelectedAnimals: (animals: SpeciesEnum[]) => void
  toggleAnimal: (species: SpeciesEnum) => void
  setDetectedPlant: (plant: PlantFull | null, confidence?: number) => void
  setRisk: (risk: RiskAssessment | null) => void
  reset: () => void
}

// ============================================================================
// CONTEXT
// ============================================================================

const initialState: FlowState = {
  photo: null,
  selectedAnimals: [],
  detectedPlant: null,
  detectedConfidence: null,
  risk: null,
}

const FlowContext = createContext<FlowContextValue | null>(null)

export function FlowProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<FlowState>(initialState)

  const setPhoto = useCallback((photo: CapturedPhoto | null) => {
    setState((s) => ({ ...s, photo }))
  }, [])

  const setSelectedAnimals = useCallback((animals: SpeciesEnum[]) => {
    setState((s) => ({ ...s, selectedAnimals: animals }))
  }, [])

  const toggleAnimal = useCallback((species: SpeciesEnum) => {
    setState((s) => {
      const has = s.selectedAnimals.includes(species)
      return {
        ...s,
        selectedAnimals: has
          ? s.selectedAnimals.filter((a) => a !== species)
          : [...s.selectedAnimals, species],
      }
    })
  }, [])

  const setDetectedPlant = useCallback(
    (plant: PlantFull | null, confidence?: number) => {
      setState((s) => ({
        ...s,
        detectedPlant: plant,
        detectedConfidence: confidence ?? s.detectedConfidence,
      }))
    },
    []
  )

  const setRisk = useCallback((risk: RiskAssessment | null) => {
    setState((s) => ({ ...s, risk }))
  }, [])

  const reset = useCallback(() => {
    // Libera o blob da foto se existir (boa prática p/ memória)
    setState((s) => {
      if (s.photo?.dataUrl.startsWith('blob:')) {
        URL.revokeObjectURL(s.photo.dataUrl)
      }
      return initialState
    })
  }, [])

  const value = useMemo<FlowContextValue>(
    () => ({
      ...state,
      setPhoto,
      setSelectedAnimals,
      toggleAnimal,
      setDetectedPlant,
      setRisk,
      reset,
    }),
    [state, setPhoto, setSelectedAnimals, toggleAnimal, setDetectedPlant, setRisk, reset]
  )

  return <FlowContext.Provider value={value}>{children}</FlowContext.Provider>
}

// ============================================================================
// HOOK
// ============================================================================

export function useFlow(): FlowContextValue {
  const ctx = useContext(FlowContext)
  if (!ctx) {
    throw new Error('useFlow deve ser usado dentro de <FlowProvider>')
  }
  return ctx
}
